import React, { useState, useEffect, useRef } from 'react';
import {
  FaCheckCircle,
  FaEnvelope,
  FaWhatsapp,
  FaUsers,
  FaFire,
  FaTag,
  FaClock,
} from 'react-icons/fa';
import SEO from '../../../components/common/SEO';
import Breadcrumb from '../../../components/common/Breadcrumb';
import PageSection from '../../../components/common/PageSection';
import { COURSE_CONTACT } from '../../../config/constants/courseData';
import { submitCreativeEnrollment, fetchEnrollmentCount } from '../../../services/creativeEnrollmentService';
import { notifySuccess, notifyWarning, notifyError } from '../../../utils/notifications';

/* ─── Countdown to Jul 1 2026 ────────────────── */
const DEADLINE = new Date('2026-07-01T00:00:00+06:00');

const useCountdown = () => {
  const [timeLeft, setTimeLeft] = useState(() => {
    const diff = DEADLINE - Date.now();
    return diff > 0 ? diff : 0;
  });

  useEffect(() => {
    if (timeLeft === 0) return;
    const id = setInterval(() => {
      const diff = DEADLINE - Date.now();
      setTimeLeft(diff > 0 ? diff : 0);
    }, 1000);
    return () => clearInterval(id);
  }, [timeLeft]);

  const seconds = Math.floor((timeLeft / 1000) % 60);
  const minutes = Math.floor((timeLeft / 1000 / 60) % 60);
  const hours   = Math.floor((timeLeft / 1000 / 60 / 60) % 24);
  const days    = Math.floor(timeLeft / 1000 / 60 / 60 / 24);

  return { days, hours, minutes, seconds, expired: timeLeft === 0 };
};

/* ─── Pad helper ──────────────────────────────── */
const pad = (n) => String(n).padStart(2, '0');

const scrollToSection = (id) => (event) => {
  event.preventDefault();
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const StaticCourseLanding = ({ course }) => {
  const rootRef = useRef(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', level: '', goal: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [enrollmentCount, setEnrollmentCount] = useState(null);

  const { days, hours, minutes, seconds, expired } = useCountdown();

  useEffect(() => {
    fetchEnrollmentCount()
      .then((total) => setEnrollmentCount(total))
      .catch(() => {/* silently ignore */});
  }, []);

  useEffect(() => {
    const elements = rootRef.current?.querySelectorAll('.course-reveal') ?? [];
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && entry.target.classList.add('is-visible')),
      { threshold: 0.08 }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handlePhoneChange = (event) => {
    const digitsOnly = event.target.value.replace(/\D/g, '');
    setForm((prev) => ({ ...prev, phone: digitsOnly }));
  };

  const handleEnroll = async (event) => {
    event.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim() || !form.level || !form.goal) {
      notifyWarning('Please fill in all fields to reserve your spot.');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await submitCreativeEnrollment({
        name: form.name,
        email: form.email,
        phone: form.phone,
        level: form.level,
        goal: form.goal,
      });
      setSubmitted(true);
      setEnrollmentCount((prev) => (prev !== null ? prev + 1 : null));
      const studentId = result?.data?.student_id;
      notifySuccess(
        studentId
          ? `Done! Your student ID is ${studentId}. We'll contact you within 24 hours with batch details.`
          : "Done! We'll contact you within 24 hours with batch details."
      );
    } catch (err) {
      notifyError(err?.message || 'Could not submit. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title={`${course.title} | Graphic Design Course | Arvix Creative`}
        description={course.description}
        keywords="graphic design course, adobe illustrator course, adobe photoshop course, creative samurai, arvix creative course, freelancing graphic design"
        image={course.heroImage}
      />

      <div ref={rootRef} className="course-details">
        <PageSection className="pt-24 sm:pt-28 md:pt-32 pb-0">
          <div>
            <Breadcrumb
              items={[{ label: "Courses", to: "/courses" }, { label: course.title }]}
              className="mb-8"
            />

            {/* Contact strip */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 sm:gap-8 mb-10 p-4 sm:p-5 bg-white/80 backdrop-blur-sm rounded-2xl border border-red-100/60 shadow-red">
              <a
                href={`mailto:${COURSE_CONTACT.email}`}
                className="flex items-center gap-3 text-gray-700 hover:text-designer-primary transition-colors"
              >
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-red-50 text-designer-primary">
                  <FaEnvelope />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-wider text-gray-400">Email Us</span>
                  <span className="text-sm font-semibold">{COURSE_CONTACT.email}</span>
                </span>
              </a>
              <div className="hidden sm:block w-px h-10 bg-red-100" />
              <a
                href={`https://wa.me/${COURSE_CONTACT.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-gray-700 hover:text-green-600 transition-colors"
              >
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-green-50 text-green-600">
                  <FaWhatsapp />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-wider text-gray-400">WhatsApp</span>
                  <span className="text-sm font-semibold">{COURSE_CONTACT.whatsappDisplay}</span>
                </span>
              </a>
            </div>

            {/* Hero */}
            <div className="relative overflow-hidden rounded-3xl border border-red-100/60 shadow-red mb-12">
              <div className="absolute inset-0">
                <img
                  src={course.heroImage}
                  alt={course.title}
                  className="w-full h-full object-cover object-right"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/20 sm:to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-t from-white/80 via-transparent to-transparent sm:hidden" />
              </div>

              <div className="relative z-10 p-6 sm:p-10 md:p-14 max-w-2xl">
                <p className="text-xs tracking-[0.25em] text-designer-primary font-semibold mb-4 uppercase">
                  {course.eyebrow}
                </p>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-designer-dark text-balance mb-2">
                  {course.heroTitle}
                  <span className="block text-designer-primary text-2xl sm:text-3xl md:text-4xl mt-2">
                    {course.subtitle}
                  </span>
                </h1>
                <p className="text-sm uppercase tracking-widest text-gray-500 mb-4">{course.tagline}</p>
                <p className="text-gray-600 leading-relaxed mb-6 max-w-xl">{course.description}</p>

                {/* Price block with discount */}
                <div className="inline-flex items-center gap-4 px-5 py-4 bg-white/90 backdrop-blur rounded-2xl border border-red-100 mb-6">
                  <div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl sm:text-5xl font-display font-bold text-designer-primary">{course.fee}</span>
                      {course.originalFee && (
                        <span className="text-lg text-gray-400 line-through font-medium">{course.originalFee}</span>
                      )}
                    </div>
                    {course.discountPercent && (
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-white bg-designer-primary px-2 py-0.5 rounded-full mt-1">
                        <FaTag className="w-2.5 h-2.5" />
                        {course.discountPercent}% OFF, Limited Time
                      </span>
                    )}
                  </div>
                  <span>
                    <span className="block text-xs uppercase tracking-wider text-gray-400">Course Fee</span>
                    <span className="text-sm font-semibold text-designer-dark">Full Access</span>
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-8">
                  {course.pills.map((pill) => (
                    <span
                      key={pill}
                      className="px-3 py-1.5 text-xs font-medium uppercase tracking-wide rounded-full bg-red-50 text-designer-primary border border-red-100"
                    >
                      {pill}
                    </span>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="#enroll"
                    onClick={scrollToSection('enroll')}
                    className="inline-flex items-center justify-center px-6 py-3.5 bg-gradient-to-r from-designer-primary to-red-600 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-red-500/30 hover:scale-[1.02] transition-all"
                  >
                    Enroll Now for {course.fee}
                  </a>
                  <a
                    href="#curriculum"
                    onClick={scrollToSection('curriculum')}
                    className="inline-flex items-center justify-center px-6 py-3.5 bg-white/90 text-designer-dark font-semibold rounded-xl border border-red-100 hover:border-designer-primary hover:text-designer-primary transition-all"
                  >
                    View Curriculum
                  </a>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-16">
              {course.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="course-reveal text-center p-5 bg-white/80 backdrop-blur-sm rounded-2xl border border-red-100/50 shadow-red"
                >
                  <div className="text-3xl md:text-4xl font-display font-bold text-designer-primary">{stat.num}</div>
                  <div className="text-xs uppercase tracking-wider text-gray-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </PageSection>

        {/* Software */}
        <PageSection className="py-12 md:py-16" gradient="none">
          <div>
            <p className="text-xs tracking-[0.25em] text-designer-primary font-semibold mb-3 uppercase">Tools You&apos;ll Master</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-designer-dark mb-10">
              Industry-Standard Software
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {course.software.map((tool) => (
                <div
                  key={tool.name}
                  className="course-reveal p-8 bg-white/80 backdrop-blur-sm rounded-2xl border border-red-100/50 shadow-red hover:-translate-y-1 transition-transform"
                >
                  <div className="text-5xl font-display font-bold text-red-100 mb-4">{tool.icon}</div>
                  <h3 className="text-2xl font-bold text-designer-dark mb-3">{tool.name}</h3>
                  <p className="text-gray-600 leading-relaxed">{tool.description}</p>
                </div>
              ))}
            </div>
          </div>
        </PageSection>

        {/* Curriculum */}
        <PageSection id="curriculum" className="py-12 md:py-16">
          <div>
            <p className="text-xs tracking-[0.25em] text-designer-primary font-semibold mb-3 uppercase">Course Structure</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-designer-dark mb-10">
              8 Weeks. Two Months. One Career
            </h2>
            <div className="grid lg:grid-cols-2 gap-6">
              {course.months.map((month) => (
                <div
                  key={month.label}
                  className="course-reveal p-6 md:p-8 bg-white/80 backdrop-blur-sm rounded-2xl border border-red-100/50 shadow-red"
                >
                  <p className="text-xs uppercase tracking-widest text-designer-primary font-semibold mb-2">{month.label}</p>
                  <h3 className="text-xl font-bold text-designer-dark mb-6">{month.title}</h3>
                  <div className="space-y-5">
                    {month.weeks.map((week) => (
                      <div key={week.title} className="border-l-2 border-designer-primary/30 pl-4">
                        <h4 className="text-sm font-bold text-designer-primary uppercase tracking-wide mb-2">{week.title}</h4>
                        <ul className="space-y-1.5">
                          {week.items.map((item) => (
                            <li key={item} className="text-sm text-gray-600 flex gap-2">
                              <span className="text-designer-primary shrink-0">•</span>
                              <span>{item}</span>
                            </li>
                          ))}
                          {week.project && (
                            <li className="text-sm font-medium text-designer-primary mt-2">
                              Project: {week.project}
                            </li>
                          )}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </PageSection>

        {/* Projects */}
        <PageSection id="projects" className="py-12 md:py-16" gradient="none">
          <div>
            <p className="text-xs tracking-[0.25em] text-designer-primary font-semibold mb-3 uppercase">What You&apos;ll Build</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-designer-dark mb-10">
              Real Projects. Real Portfolio
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {course.projects.map((project) => (
                <div
                  key={project.num}
                  className="course-reveal relative p-6 bg-white/80 backdrop-blur-sm rounded-2xl border border-red-100/50 shadow-red hover:-translate-y-1 transition-all"
                >
                  <span className="absolute top-4 right-4 text-5xl font-display font-bold text-red-50">{project.num}</span>
                  <p className="text-xs uppercase tracking-wider text-designer-primary font-semibold mb-2">{project.tag}</p>
                  <h3 className="text-lg font-bold text-designer-dark mb-2">{project.name}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{project.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </PageSection>

        {/* Outcomes */}
        <PageSection className="py-12 md:py-16">
          <div>
            <p className="text-xs tracking-[0.25em] text-designer-primary font-semibold mb-3 uppercase">After This Course</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-designer-dark mb-10">
              What You&apos;ll Walk Away With
            </h2>
            <div className="grid lg:grid-cols-2 gap-8 items-start">
              <div className="space-y-1">
                {course.outcomes.map((outcome) => (
                  <div
                    key={outcome}
                    className="course-reveal flex gap-3 py-4 border-b border-red-100/60 last:border-0"
                  >
                    <FaCheckCircle className="w-5 h-5 text-designer-primary shrink-0 mt-0.5" />
                    <p className="text-gray-700 leading-relaxed">{outcome}</p>
                  </div>
                ))}
              </div>
              <div className="course-reveal p-6 md:p-8 bg-white/80 backdrop-blur-sm rounded-2xl border border-red-100/50 shadow-red border-t-4 border-t-designer-primary">
                <h3 className="text-2xl font-display font-bold text-designer-primary mb-5">Bonus Classes</h3>
                <ul className="space-y-3">
                  {course.bonus.map((item) => (
                    <li key={item} className="flex gap-2 text-gray-600 text-sm">
                      <span className="text-designer-primary font-bold">+</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </PageSection>

        {/* Instructor */}
        <PageSection id="instructor" className="py-12 md:py-16" gradient="none">
          <div>
            <p className="text-xs tracking-[0.25em] text-designer-primary font-semibold mb-3 uppercase">Meet Your Instructor</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-designer-dark mb-10">Your Instructor</h2>
            <div className="course-reveal grid lg:grid-cols-[320px_1fr] gap-10 items-center p-6 md:p-10 bg-white/80 backdrop-blur-sm rounded-3xl border border-red-100/50 shadow-red">
              <div className="relative mx-auto lg:mx-0 w-full max-w-[280px]">
                <div className="absolute -inset-2 bg-gradient-to-br from-designer-primary/20 to-transparent rounded-2xl blur-xl" />
                <img
                  src={course.instructor.image}
                  alt={`${course.instructor.name}, ${course.instructor.title}`}
                  className="relative w-full rounded-2xl object-cover aspect-[3/4] border border-red-100 shadow-lg"
                />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-designer-primary font-semibold mb-2">
                  {course.instructor.badge}
                </p>
                <h3 className="text-3xl md:text-4xl font-display font-bold text-designer-dark mb-1">
                  {course.instructor.name}
                </h3>
                <p className="text-sm uppercase tracking-wider text-gray-500 mb-4">{course.instructor.title}</p>
                <a
                  href={`mailto:${COURSE_CONTACT.email}`}
                  className="inline-flex items-center gap-2 text-sm text-designer-primary font-medium mb-5 hover:underline"
                >
                  <FaEnvelope className="w-3.5 h-3.5" />
                  {COURSE_CONTACT.email}
                </a>
                <p className="text-gray-600 leading-relaxed mb-6 max-w-2xl">{course.instructor.bio}</p>
                <div className="flex flex-wrap gap-3 mb-6">
                  {course.instructor.stats.map((stat) => (
                    <div key={stat.label} className="px-4 py-3 bg-red-50 rounded-xl text-center min-w-[100px]">
                      <div className="text-2xl font-display font-bold text-designer-primary">{stat.num}</div>
                      <div className="text-[10px] uppercase tracking-wider text-gray-500">{stat.label}</div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  {course.instructor.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs uppercase tracking-wide rounded-full bg-red-50 text-gray-600 border border-red-100"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </PageSection>

        {/* ═══ Enroll Section ══════════════════════════════════ */}
        <PageSection id="enroll" className="py-12 md:py-20 pb-24">
          <div>
            <div className="text-center mb-10">
              <p className="text-xs tracking-[0.25em] text-designer-primary font-semibold mb-3 uppercase">Ready to Start?</p>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-designer-dark mb-4">Claim Your Seat</h2>
              <p className="text-gray-600 leading-relaxed max-w-xl mx-auto">
                Seats fill up fast. Fill out the form below and we&apos;ll confirm your spot within 24 hours.
                No payment required to reserve.
              </p>
            </div>

            <div className="grid lg:grid-cols-[1fr_400px] gap-8 items-start">

              {/* ── Left: Sidebar info ── */}
              <div className="space-y-5 order-2 lg:order-1">

                {/* Price Card with Discount */}
                <div className="p-6 bg-white/90 backdrop-blur-sm rounded-2xl border border-red-100 shadow-red">
                  <div className="flex items-center gap-2 mb-3">
                    <FaTag className="text-designer-primary w-4 h-4" />
                    <span className="text-xs font-bold uppercase tracking-wider text-designer-primary">
                      Limited-Time Offer
                    </span>
                  </div>
                  <div className="flex items-baseline gap-3 mb-2">
                    <span className="text-4xl font-display font-bold text-designer-primary">{course.fee}</span>
                    {course.originalFee && (
                      <span className="text-xl text-gray-400 line-through">{course.originalFee}</span>
                    )}
                    {course.discountPercent && (
                      <span className="ml-auto text-sm font-bold text-white bg-designer-primary px-2.5 py-1 rounded-full">
                        -{course.discountPercent}%
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">/ Complete Course · Full Access</p>
                  {course.discountPercent && course.originalFee && (
                    <div className="mt-3 p-3 bg-red-50 rounded-xl border border-red-100">
                      <p className="text-xs text-designer-primary font-semibold">
                        You save {course.originalFee.replace('৳ ', '৳')} → {course.fee.replace('৳ ', '৳')}. That&apos;s {course.discountPercent}% off the regular price!
                      </p>
                    </div>
                  )}
                </div>

                {/* Enrollment Count */}
                {enrollmentCount !== null && (
                  <div className="flex items-center gap-4 p-5 bg-white/90 backdrop-blur-sm rounded-2xl border border-red-100 shadow-red">
                    <div className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center text-designer-primary shrink-0">
                      <FaUsers className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-2xl font-display font-bold text-designer-dark leading-none">
                        {enrollmentCount}
                      </p>
                      <p className="text-sm text-gray-500 mt-0.5">students already enrolled</p>
                    </div>
                    <FaFire className="text-orange-400 w-5 h-5 ml-auto animate-pulse" />
                  </div>
                )}

                {/* Countdown Timer */}
                <div className="p-5 bg-designer-dark rounded-2xl border border-white/5 relative overflow-hidden">
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_70%_at_50%_0%,rgba(227,28,37,0.2),transparent)]" />
                  <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-3">
                      <FaClock className="text-designer-primary w-3.5 h-3.5" />
                      <span className="text-xs font-bold uppercase tracking-wider text-white/60">
                        {expired ? 'Offer Ended' : 'Offer ends Jul 1, 2026'}
                      </span>
                    </div>
                    {expired ? (
                      <p className="text-sm text-white/50">The early-bird offer has ended.</p>
                    ) : (
                      <div className="grid grid-cols-4 gap-2">
                        {[
                          { label: 'Days',  val: days },
                          { label: 'Hours', val: hours },
                          { label: 'Mins',  val: minutes },
                          { label: 'Secs',  val: seconds },
                        ].map(({ label, val }) => (
                          <div key={label} className="text-center bg-white/8 rounded-xl py-3 px-1">
                            <p className="text-2xl font-mono font-bold text-white leading-none">{pad(val)}</p>
                            <p className="text-[10px] text-white/40 mt-1 uppercase tracking-wider">{label}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

              </div>

              {/* ── Right: Form ── */}
              <div className="order-1 lg:order-2">
                <form
                  onSubmit={handleEnroll}
                  className="p-6 md:p-8 bg-white/90 backdrop-blur-sm rounded-2xl border border-red-100/50 shadow-red space-y-4"
                >
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="f-name" className="block text-xs uppercase tracking-wider text-gray-500 mb-2">Full Name</label>
                      <input
                        id="f-name"
                        type="text"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-designer-primary focus:ring-2 focus:ring-designer-primary/20 outline-none transition"
                        placeholder="Your full name"
                        value={form.name}
                        onChange={handleChange('name')}
                        disabled={submitted}
                      />
                    </div>
                    <div>
                      <label htmlFor="f-phone" className="block text-xs uppercase tracking-wider text-gray-500 mb-2">Phone Number</label>
                      <input
                        id="f-phone"
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-designer-primary focus:ring-2 focus:ring-designer-primary/20 outline-none transition"
                        placeholder="01234567890"
                        value={form.phone}
                        onChange={handlePhoneChange}
                        disabled={submitted}
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="f-email" className="block text-xs uppercase tracking-wider text-gray-500 mb-2">Email Address</label>
                    <input
                      id="f-email"
                      type="email"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-designer-primary focus:ring-2 focus:ring-designer-primary/20 outline-none transition"
                      placeholder="you@email.com"
                      value={form.email}
                      onChange={handleChange('email')}
                      disabled={submitted}
                    />
                  </div>
                  <div>
                    <label htmlFor="f-level" className="block text-xs uppercase tracking-wider text-gray-500 mb-2">Experience Level</label>
                    <select
                      id="f-level"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-designer-primary focus:ring-2 focus:ring-designer-primary/20 outline-none transition bg-white"
                      value={form.level}
                      onChange={handleChange('level')}
                      disabled={submitted}
                    >
                      <option value="">Select your level</option>
                      <option>Complete Beginner</option>
                      <option>Some Design Knowledge</option>
                      <option>Used Photoshop Before</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="f-goal" className="block text-xs uppercase tracking-wider text-gray-500 mb-2">Your Goal</label>
                    <select
                      id="f-goal"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-designer-primary focus:ring-2 focus:ring-designer-primary/20 outline-none transition bg-white"
                      value={form.goal}
                      onChange={handleChange('goal')}
                      disabled={submitted}
                    >
                      <option value="">What brings you here?</option>
                      <option>Start Freelancing</option>
                      <option>Get a Design Job</option>
                      <option>Build My Own Brand</option>
                      <option>Passion & Creativity</option>
                    </select>
                  </div>
                  <button
                    type="submit"
                    disabled={submitted || isSubmitting}
                    className="w-full py-3.5 bg-gradient-to-r from-designer-primary to-red-600 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-red-500/30 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {submitted
                      ? "✓ Done! We'll contact you within 24 hours."
                      : isSubmitting
                        ? 'Submitting...'
                        : `Reserve My Spot for ${course.fee} →`}
                  </button>
                  <p className="text-xs text-gray-400 text-center">
                    No payment required to reserve. Or email{' '}
                    <a href={`mailto:${COURSE_CONTACT.email}`} className="text-designer-primary hover:underline">
                      {COURSE_CONTACT.email}
                    </a>
                  </p>
                </form>
              </div>

            </div>
          </div>
        </PageSection>
      </div>

      <style>{`
        .course-reveal {
          opacity: 0;
          transform: translateY(16px);
          transition: opacity 0.45s ease, transform 0.45s ease;
        }
        .course-reveal.is-visible {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </>
  );
};

export default StaticCourseLanding;
